import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { adminApi, SALE_STATUS_LABEL, type SaleDetail as SaleDetailData } from "@/lib/admin";
import { formatPln } from "@/lib/utils";

function ReplayButton({ saleId }: { saleId: string }) {
  const [state, setState] = useState<"idle" | "running" | "done" | "error">("idle");
  const [info, setInfo] = useState<string | null>(null);

  async function go() {
    if (state === "running") return;
    setState("running");
    setInfo("Wysyłam do voice backendu…");
    try {
      const r = await adminApi.replaySale(saleId);
      if (r.success) {
        setState("done");
        setInfo(`Replay wysłany — ${r.connectedDevices} ESP, ${r.durationMs}ms`);
      } else {
        setState("error");
        setInfo(r.error || "Replay się nie udał");
      }
    } catch (e) {
      setState("error");
      setInfo((e as Error).message);
    } finally {
      setTimeout(() => {
        setState("idle");
        setInfo(null);
      }, 5000);
    }
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <button
        type="button"
        onClick={go}
        disabled={state === "running"}
        className="btn-ghost text-xs disabled:opacity-50 !bg-carp-ink !text-carp-yellow hover:!bg-carp-primary hover:!text-white"
        title="Wyślij ten sam tekst odpowiedzi raz jeszcze do podpiętych ESP + OBS overlay"
      >
        {state === "running" ? "▶ replay…" : state === "done" ? "✓ wysłano" : state === "error" ? "✗ błąd" : "▶ Replay"}
      </button>
      {info && <p className="text-[10px] font-mono text-carp-ink/70">{info}</p>}
    </div>
  );
}

// One-click VAT invoice issue for this specific sale. Calls POST /api/sales/{id}/invoice
// which routes through the same IssueInvoiceForSaleCommand path as production
// auto-issue (post-answered). Goes to PROD Subiekt — every successful click
// creates a real document, so the button shows the invoice id loud + offers
// reload to refresh the sale timeline.
function IssueInvoiceButton({
  saleId,
  alreadyHas,
  onIssued,
}: {
  saleId: string;
  alreadyHas: boolean;
  onIssued: () => void;
}) {
  const [state, setState] = useState<"idle" | "running" | "done" | "error">("idle");
  const [info, setInfo] = useState<string | null>(null);

  async function go() {
    if (state === "running") return;
    if (alreadyHas && !window.confirm("Ta sprzedaż już ma fakturę. Wystawić jeszcze raz?")) return;
    setState("running");
    setInfo("Wystawiam fakturę w Subiekt…");
    try {
      const r = await adminApi.issueInvoice(saleId);
      setState("done");
      setInfo(`Wystawiono: ${r.invoiceId}`);
      onIssued();
    } catch (e) {
      setState("error");
      setInfo((e as Error).message);
    } finally {
      setTimeout(() => {
        setState("idle");
        setInfo(null);
      }, 8000);
    }
  }

  return (
    <div className="flex flex-col items-start gap-1">
      <button
        type="button"
        onClick={go}
        disabled={state === "running"}
        className="btn-primary text-xs disabled:opacity-50"
        title={
          alreadyHas
            ? "Sprzedaż ma już fakturę. Klik wystawi kolejną — używaj świadomie."
            : "Wystaw VAT fakturę dla tej sprzedaży (prod Subiekt 123, prawdziwy dokument)."
        }
      >
        {state === "running"
          ? "Wystawiam…"
          : state === "done"
          ? "✓ wystawiono"
          : state === "error"
          ? "✗ błąd"
          : alreadyHas
          ? "Wystaw kolejną fakturę"
          : "Wystaw fakturę VAT"}
      </button>
      {info && <p className="text-[10px] font-mono text-carp-ink/70">{info}</p>}
    </div>
  );
}

export function AdminSaleDetail() {
  const { id } = useParams<{ id: string }>();
  const [sale, setSale] = useState<SaleDetailData | null>(null);
  const [err, setErr] = useState<string | null>(null);

  function refetch() {
    if (!id) return;
    adminApi.saleDetail(id).then(setSale).catch((e) => setErr(e.message));
  }

  useEffect(() => {
    if (!id) return;
    let active = true;
    adminApi
      .saleDetail(id)
      .then((d) => active && setSale(d))
      .catch((e) => active && setErr(e.message));
    return () => {
      active = false;
    };
  }, [id]);

  if (err) return <ErrorBox message={err} />;
  if (!sale) return <p className="font-mono text-sm">Ładuję sprzedaż…</p>;

  return (
    <div className="space-y-4">
      <Link to="/admin/sales" className="link-brut text-sm">
        ← wróć do listy
      </Link>

      <header className="brut bg-carp-paper p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display font-bold text-3xl">
              {formatPln(sale.amount)}
            </h1>
            <p className="text-sm text-carp-ink/70">
              {sale.customerName ? <b>{sale.customerName}</b> : <i>Anonim</i>} ·{" "}
              {sale.email}
            </p>
          </div>
          <div className="text-right">
            <span className="chip bg-carp-cyan">
              {SALE_STATUS_LABEL[sale.status] ?? sale.status}
            </span>
            <p className="text-xs text-carp-ink/60 font-mono mt-1">
              {sale.providerKey} · {sale.providerSessionId ?? "—"}
            </p>
          </div>
        </div>
      </header>

      <section className="brut bg-carp-paper p-5">
        <h2 className="font-display font-bold uppercase text-xs tracking-wide text-carp-ink/60 mb-2">
          Wiadomość donatora
        </h2>
        <p className="leading-relaxed">{sale.message}</p>
      </section>

      {sale.responseText && (
        <section className="brut bg-carp-yellow p-5">
          <div className="flex items-start justify-between gap-4 mb-2">
            <h2 className="font-display font-bold uppercase text-xs tracking-wide text-carp-ink/60">
              Odpowiedź MudCarpa
            </h2>
            <ReplayButton saleId={sale.id} />
          </div>
          <p className="leading-relaxed font-display">{sale.responseText}</p>
        </section>
      )}

      {sale.failureReason && (
        <section className="brut bg-carp-destructive text-white p-5">
          <h2 className="font-display font-bold uppercase text-xs tracking-wide opacity-80 mb-2">
            Powód niepowodzenia
          </h2>
          <p className="text-sm">{sale.failureReason}</p>
        </section>
      )}

      <section className="brut bg-carp-paper p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
          <h2 className="font-display font-bold text-lg">Faktura VAT</h2>
          <IssueInvoiceButton
            saleId={sale.id}
            alreadyHas={!!sale.invoiceId}
            onIssued={refetch}
          />
        </div>
        {sale.invoiceId ? (
          <p className="text-sm font-mono">
            Wystawiono: <b>{sale.invoiceId}</b>
            {sale.invoiceIssuedAt && (
              <span className="text-carp-ink/60">
                {" "}({new Date(sale.invoiceIssuedAt).toLocaleString("pl-PL")})
              </span>
            )}
          </p>
        ) : (
          <p className="text-sm text-carp-ink/60">
            Brak — kliknij przycisk żeby wystawić w Subiekt 123 (uwaga: prawdziwa faktura).
          </p>
        )}
      </section>

      <section className="brut bg-carp-paper p-5">
        <h2 className="font-display font-bold text-lg mb-3">Timeline</h2>
        <ul className="text-sm space-y-1 font-mono">
          <Tl label="Created" date={sale.createdAt} />
          <Tl label="Paid" date={sale.paidAt} />
          <Tl label="Answered" date={sale.answeredAt} />
          <Tl label="Failed" date={sale.failedAt} />
          {sale.invoiceIssuedAt && (
            <Tl label={`Invoice issued (${sale.invoiceId})`} date={sale.invoiceIssuedAt} />
          )}
        </ul>
      </section>

      <section className="brut bg-carp-paper p-5">
        <h2 className="font-display font-bold text-lg mb-3">
          Outbound HTTP ({sale.outbound.length})
        </h2>
        <table className="w-full text-xs">
          <thead className="text-left font-mono uppercase text-carp-ink/60">
            <tr>
              <th className="pb-2">Czas</th>
              <th className="pb-2">Cel</th>
              <th className="pb-2">Metoda</th>
              <th className="pb-2">URL</th>
              <th className="pb-2 text-right">Status</th>
              <th className="pb-2 text-right">ms</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-carp-ink/10">
            {sale.outbound.map((c) => (
              <tr key={c.id} className={c.errorMessage ? "bg-carp-destructive/10" : ""}>
                <td className="py-1 font-mono text-[11px] text-carp-ink/60 whitespace-nowrap">
                  {new Date(c.timestamp).toLocaleTimeString("pl-PL")}
                </td>
                <td className="py-1 font-bold">{c.target}</td>
                <td className="py-1">{c.method}</td>
                <td className="py-1 font-mono text-[11px] truncate max-w-[280px]">
                  {c.url}
                </td>
                <td className="py-1 text-right font-mono">{c.statusCode ?? "—"}</td>
                <td className="py-1 text-right font-mono">{c.durationMs}</td>
              </tr>
            ))}
            {sale.outbound.length === 0 && (
              <tr>
                <td colSpan={6} className="py-3 text-center text-carp-ink/50">
                  Brak wywołań HTTP w przedziale czasowym
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function Tl({ label, date }: { label: string; date: string | null }) {
  if (!date) return null;
  return (
    <li>
      <b>{label}</b> · {new Date(date).toLocaleString("pl-PL")}
    </li>
  );
}

function ErrorBox({ message }: { message: string }) {
  return (
    <div className="brut bg-carp-destructive text-white p-4">
      <p className="font-display font-bold uppercase">Błąd</p>
      <p className="text-sm">{message}</p>
    </div>
  );
}
